import React from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import { PERSONAL } from 'remote/channels/types'
import {
  TEMPLATE_NOTES,
  BOOKMARKED_NOTES,
  ARCHIVED_NOTES,
} from 'data/filters/types'

import { getAllNotes, getAllTemplateNotes } from 'data/notes/selectors'
import {
  getAccessibleChannels,
  getPersonalChannel,
} from 'data/channels/selectors'
import {
  getChannelFavoriteIds,
  getBookmarkedNoteIds,
} from 'data/userPreferences/selectors'

import { NoteItem } from '../Items/Note'
import { ChannelItem } from '../Items/Channel'
import { ArchivedNoteItem } from '../Items/ArchivedNote'
import { EnhancedTemplateNoteItem } from '../Items/TemplateNote'
import { ARCHIVED_SECTION_ID, ArchivedFilter } from '../Items/Filters/Archived'
import {
  TEMPLATE_NOTES_SECTION_ID,
  TemplatesFilter,
} from '../Items/Filters/Templates'
import { ArchivedNotesPlaceholder } from '../Items/Filters/ArchivedNotesPlaceholder'

import { ImportLink } from '../Items/ImportLink'
import {
  BookmarkedNotesLoader,
  NoteChildrenLoader,
  ChannelNotesLoader,
  TemplateNotesLoader,
  ArchivedNotesLoader,
} from '../Items/Loaders'

import {
  BOOKMARKED_NOTES_SECTION_ID,
  BookmarkedNotesSectionHeader,
} from '../Items/SectionHeader/BookmarkedNotesSectionHeader'
import {
  FAVORITE_CHANNELS_SECTION_ID,
  FavoriteChannelsSectionHeader,
} from '../Items/SectionHeader/FavoriteChannelSectionHeader'
import {
  ALL_CHANNELS_SECTION_ID,
  AllChannelsSectionHeader,
} from '../Items/SectionHeader/AllChannelsSectionHeader'

export default function useEntries({
  activeEntryId,
  expandedEntryIds,
  onToggleExpand,
  setLastClickedNoteId,
}) {
  const favoriteChannelIds = useSelector(getChannelFavoriteIds)
  const bookmarkedNoteIds = useSelector(getBookmarkedNoteIds)
  const channelsFromStore = useSelector(getAccessibleChannels)
  const personalChannel = useSelector(getPersonalChannel)
  const notesFromStore = useSelector(getAllNotes)
  const templateNotes = useSelector(getAllTemplateNotes)

  const archivedNotesSelector = React.useMemo(() => {
    return createSelector(getAllNotes, allNotes =>
      allNotes.filter(note => note.archivedAt).toArray()
    )
  }, [])
  const allArchivedNotes = useSelector(archivedNotesSelector)

  const makeEntry = React.useMemo(
    () => {
      return function makeEntry({
        id,
        sectionKey,
        depth,
        filterId = '',
        isExpandable = false,
        renderLayout = undefined,
        dndData,
      }) {
        // same entity can appear several times in the structure (as search result + nested in another search result)
        const key = filterId + sectionKey + id + depth

        return {
          id,
          depth,
          key,
          isExpandable,
          isActive: activeEntryId === id,
          isExpanded: expandedEntryIds.has(id),
          onToggleExpand,
          dndData,
          renderLayout,
        }
      }
    },
    // this props are shared by all entries, we'll just spread this bag everywhere
    [activeEntryId, expandedEntryIds, onToggleExpand]
  )

  const makeNoteEntries = React.useMemo(
    () =>
      function makeNoteEntries({
        depth,
        id,
        sectionKey,
        filterId,
        ancestorNoteIds = [],
        ...noteProps
      }) {
        const dndData = {
          type: 'note',
          title: noteProps.title,
          sectionKey,
          noteId: id,
          channelId: noteProps.channelId,
          listPosition: noteProps.listPosition,
          ancestorNoteIds,
        }

        const noteEntry = makeEntry({
          id,
          sectionKey,
          depth,
          filterId,
          dndData,
          isExpandable: noteProps.hasChildren,
          renderLayout(entryProps) {
            return (
              <NoteItem
                {...entryProps}
                {...noteProps}
                setLastClickedNoteId={setLastClickedNoteId}
                filterId={filterId}
                dndData={dndData}
              />
            )
          },
        })

        const noteIsExpanded = noteEntry.isExpanded
        if (!noteIsExpanded || !noteProps.hasChildren) {
          return [noteEntry]
        }

        const noteChildren = notesFromStore
          .filter(
            note =>
              note.parentNote &&
              note.parentNote.get('id') === id &&
              !note.archivedAt
          )
          .sort((noteA, noteB) => {
            return noteB.listPosition - noteA.listPosition
          })
          .toArray()

        const noteChildrenAncestorNoteIds = ancestorNoteIds.concat(id)
        const noteChildrenEntries = noteChildren.reduce(
          (noteChildrenEntries, note) => {
            const {
              id,
              hasChildren,
              iconShape,
              iconColor,
              title,
              channelId,
              listPosition,
            } = note

            return noteChildrenEntries.concat(
              makeNoteEntries({
                depth: depth + 1,
                id,
                sectionKey: noteEntry.key,
                listPosition,
                hasChildren,
                iconShape,
                iconColor,
                title,
                channelId,
                filterId,
                ancestorNoteIds: noteChildrenAncestorNoteIds,
              })
            )
          },
          []
        )

        const noteChildrenLoader = makeEntry({
          renderLayout() {
            return <NoteChildrenLoader noteId={id} />
          },
          id: `note-${id}-loader`,
          filterId,
          depth: depth + 1,
        })
        return [noteEntry, ...noteChildrenEntries, noteChildrenLoader]
      },
    [makeEntry, notesFromStore, setLastClickedNoteId]
  )

  const makeBookmarkedEntries = React.useCallback(
    () => {
      if (bookmarkedNoteIds.size === 0) {
        return []
      }

      const bookmarkedNotesHeaderEntry = makeEntry({
        isExpandable: true,
        id: BOOKMARKED_NOTES_SECTION_ID,
        depth: 0,
        renderLayout(entryProps) {
          return <BookmarkedNotesSectionHeader {...entryProps} />
        },
      })

      const bookmarkedNotesSectionIsExpanded = expandedEntryIds.has(
        BOOKMARKED_NOTES_SECTION_ID
      )

      const bookmarkedNotesLoader = makeEntry({
        renderLayout() {
          return <BookmarkedNotesLoader />
        },
        id: `bookmarked-notes-loader`,
        depth: 1,
      })

      const bookmarkedNoteEntries = bookmarkedNotesSectionIsExpanded
        ? notesFromStore
            .filter(
              note => bookmarkedNoteIds.includes(note.id) && !note.archivedAt
            )
            .reduce(
              function(
                noteEntries,
                { id, hasChildren, iconShape, iconColor, title, channelId }
              ) {
                return noteEntries.concat(
                  makeNoteEntries({
                    depth: 1,
                    id,
                    sectionKey: bookmarkedNotesHeaderEntry.key,
                    hasChildren,
                    iconShape,
                    iconColor,
                    title,
                    channelId,
                    filterId: BOOKMARKED_NOTES.id,
                    onlyAllowDropInside: true, // prenvent re-ordering around top level bookmarked notes
                  })
                )
              },
              [bookmarkedNotesLoader]
            )
        : []

      return [bookmarkedNotesHeaderEntry].concat(bookmarkedNoteEntries)
    },
    [
      makeEntry,
      makeNoteEntries,
      expandedEntryIds,
      bookmarkedNoteIds,
      notesFromStore,
    ]
  )

  const makeArchivedNotesEntries = React.useMemo(
    () =>
      function makeArchivedNotesEntries({
        depth = 0,
        parentNoteId,
        sectionKey = ARCHIVED_SECTION_ID,
      } = {}) {
        const archivedNotesForGivenParentNote = allArchivedNotes
          .filter(archivedNote => {
            if (!parentNoteId) {
              if (!archivedNote.parentNote) {
                return true
              }
              const parentNote = notesFromStore.find(
                note => note.id === archivedNote.parentNote.get('id')
              )
              // If parent node is not fetched, this means that its not a root archived node
              // But if node is fetched due to any other action,make its not archived
              return !parentNote || !parentNote.archivedAt
            }
            return (
              archivedNote.parentNote &&
              archivedNote.parentNote.get('id') === parentNoteId
            )
          })
          .sort(function byDescArchivedAt(a, b) {
            return new Date(b.archivedAt) - new Date(a.archivedAt)
          })

        const archivedNoteLoader = makeEntry({
          renderLayout() {
            return (
              <ArchivedNotesLoader
                noteId={parentNoteId ? parentNoteId : undefined}
              />
            )
          },
          id: `archived-note-${parentNoteId}-loader`,
          depth: depth + 1,
        })

        if (archivedNotesForGivenParentNote.length === 0) {
          return [
            ...(parentNoteId
              ? []
              : [
                  makeEntry({
                    renderLayout() {
                      return <ArchivedNotesPlaceholder />
                    },
                    id: `archived-note-empty-placeholder`,
                    depth: depth + 1,
                  }),
                ]),
            archivedNoteLoader,
          ]
        }

        let archivedNotesEntries
        if (archivedNotesForGivenParentNote.length) {
          archivedNotesEntries = archivedNotesForGivenParentNote.reduce(
            (entries, archivedNote) => {
              const {
                id,
                title,
                channelId,
                iconShape,
                iconColor,
                archivedAt,
                hasChildren,
              } = archivedNote

              const archivedNoteProps = {
                id,
                title,
                channelId,
                iconShape,
                iconColor,
                archivedAt,
              }

              const archivedNoteEntry = makeEntry({
                id,
                sectionKey,
                depth,
                filterId: ARCHIVED_NOTES.id,
                isExpandable: hasChildren,
                renderLayout(entryProps) {
                  return (
                    <ArchivedNoteItem {...entryProps} {...archivedNoteProps} />
                  )
                },
              })
              const archivedNoteIsExpanded =
                expandedEntryIds.has(archivedNoteEntry.key) ||
                expandedEntryIds.has(archivedNoteEntry.id)

              if (!archivedNoteIsExpanded || !hasChildren) {
                return [...entries, archivedNoteEntry]
              }
              const childrenEntries = makeArchivedNotesEntries({
                depth: depth + 1,
                parentNoteId: archivedNote.id,
                sectionKey,
              })
              const archivedNoteChildrenLoader = makeEntry({
                renderLayout() {
                  return <ArchivedNotesLoader noteId={archivedNote.id} />
                },
                id: `archived-note-${id}-loader`,
                depth: depth + 1,
              })
              return [
                ...entries,
                archivedNoteEntry,
                ...childrenEntries,
                archivedNoteChildrenLoader,
              ]
            },
            []
          )
        } else {
          archivedNotesEntries = [
            makeEntry({
              renderLayout() {
                return <ArchivedNotesPlaceholder />
              },
              id: `archived-note-empty-placeholder`,
              depth: depth + 1,
            }),
          ]
        }

        return archivedNotesEntries.concat(archivedNoteLoader)
      },
    [allArchivedNotes, expandedEntryIds, makeEntry, notesFromStore]
  )

  const makeChannelEntries = React.useMemo(
    () => {
      return function({ depth, id, name, type, details, filterId }) {
        const channelEntry = makeEntry({
          id,
          depth: 0,
          isExpandable: true,
          renderLayout(entryProps) {
            return (
              <ChannelItem
                {...entryProps}
                name={name}
                type={type}
                details={details}
                setLastClickedNoteId={setLastClickedNoteId}
              />
            )
          },
        })

        const channelIsExpanded = expandedEntryIds.has(id)
        if (!channelIsExpanded) {
          return [channelEntry]
        }

        const notesForChannel = notesFromStore.filter(
          note => note.channelId === id && !note.archivedAt && !note.parentNote
        )
        const noteEntries = notesForChannel
          .sort((noteA, noteB) => {
            return noteB.listPosition - noteA.listPosition
          })
          .reduce(function(
            noteEntries,
            {
              id,
              hasChildren,
              iconShape,
              iconColor,
              title,
              channelId,
              listPosition,
            }
          ) {
            return noteEntries.concat(
              makeNoteEntries({
                depth: depth + 1,
                id,
                sectionKey: channelEntry.key,
                listPosition,
                hasChildren,
                iconShape,
                iconColor,
                title,
                channelId,
                filterId,
              })
            )
          },
          [])

        return [channelEntry].concat(noteEntries).concat(
          makeEntry({
            renderLayout() {
              return <ChannelNotesLoader channelId={id} />
            },
            id: `channel-${id}-loader`,
            depth: depth + 1,
          })
        )
      }
    },
    [
      makeEntry,
      makeNoteEntries,
      notesFromStore,
      expandedEntryIds,
      setLastClickedNoteId,
    ]
  )

  const scrollableEntries = React.useMemo(
    () => {
      const bookmarksEntries = makeBookmarkedEntries()

      const [
        regularChannels,
        favoriteChannels,
      ] = channelsFromStore.toArray().reduce(
        ([regularChannels, favoriteChannels], channel) => {
          if (channel.id === personalChannel.id) {
            return [regularChannels, favoriteChannels]
          }
          if (favoriteChannelIds.includes(channel.id)) {
            return [regularChannels, favoriteChannels.concat(channel)]
          }
          return [regularChannels.concat(channel), favoriteChannels]
        },
        [[], []]
      )

      const favoriteChannelsHeaderEntry = makeEntry({
        isExpandable: true,
        renderLayout(entryProps) {
          return (
            <FavoriteChannelsSectionHeader
              {...entryProps}
              favoritesCount={favoriteChannels.length + 2} // + 2 > bookmarks section and personal channel
            />
          )
        },
        id: FAVORITE_CHANNELS_SECTION_ID,
        depth: 0,
      })

      const personalChannelEntries = makeChannelEntries({
        depth: 0,
        id: personalChannel.id,
        name: personalChannel.name,
        type: personalChannel.type,
      })

      const favoriteChannelsSectionIsExpanded = expandedEntryIds.has(
        FAVORITE_CHANNELS_SECTION_ID
      )

      const favoriteChannelEntries = favoriteChannelsSectionIsExpanded
        ? favoriteChannels
            .sort((a, b) => a.get('name').localeCompare(b.get('name')))
            .reduce(function(entries, channelRecord) {
              const { id, name, type } = channelRecord
              const channelEntries = makeChannelEntries({
                depth: 0,
                id,
                name,
                type,
                filterId: BOOKMARKED_NOTES.id,
              })
              return entries.concat(channelEntries)
            }, bookmarksEntries)
        : []

      const favoriteEntries = [favoriteChannelsHeaderEntry].concat(
        favoriteChannelEntries
      )

      let allChannelsEntries = regularChannels
        .sort((a, b) => a.get('name').localeCompare(b.get('name')))
        .filter(channel => channel.type !== PERSONAL)
        .reduce(function(entries, channelRecord) {
          const { id, name, type } = channelRecord
          const channelEntries = makeChannelEntries({
            depth: 0,
            id,
            name,
            type,
          })
          return entries.concat(channelEntries)
        }, [])

      const allChannelsHeaderEntry = makeEntry({
        isExpandable: true,
        renderLayout(entryProps) {
          return (
            <AllChannelsSectionHeader
              {...entryProps}
              channelsCount={regularChannels.length}
            />
          )
        },
        id: ALL_CHANNELS_SECTION_ID,
        depth: 0,
      })

      const allChannelsSectionIsExpanded = expandedEntryIds.has(
        ALL_CHANNELS_SECTION_ID
      )
      allChannelsEntries = [allChannelsHeaderEntry].concat(
        allChannelsSectionIsExpanded ? allChannelsEntries : []
      )

      return personalChannelEntries.concat(favoriteEntries, allChannelsEntries)
    },
    [
      makeEntry,
      makeChannelEntries,
      makeBookmarkedEntries,
      channelsFromStore,
      favoriteChannelIds,
      expandedEntryIds,
      personalChannel,
    ]
  )

  const templateNotesSectionIsExpanded = expandedEntryIds.has(
    TEMPLATE_NOTES_SECTION_ID
  )
  const templateNotesSection = makeEntry({
    id: TEMPLATE_NOTES_SECTION_ID,
    depth: 0,
    isExpandable: true,
    renderLayout(entryProps) {
      return <TemplatesFilter {...entryProps} />
    },
  })

  const templateNotesEntries = templateNotesSectionIsExpanded
    ? [
        templateNotesSection,
        makeEntry({
          renderLayout() {
            return <TemplateNotesLoader />
          },
          id: `${TEMPLATE_NOTES_SECTION_ID}-loader`,
          depth: 1,
        }),
        ...templateNotes.map(template => {
          return makeEntry({
            id: template.id,
            sectionKey: TEMPLATE_NOTES_SECTION_ID,
            depth: 1,
            filterId: TEMPLATE_NOTES.id,
            isExpandable: false,
            renderLayout(entryProps) {
              return (
                <EnhancedTemplateNoteItem
                  id={template.id}
                  title={template.title}
                  {...entryProps}
                />
              )
            },
          })
        }),
      ]
    : [templateNotesSection]

  const archivedNotesSectionIsExpanded = expandedEntryIds.has(
    ARCHIVED_SECTION_ID
  )
  const archivedNotesSection = makeEntry({
    id: ARCHIVED_SECTION_ID,
    depth: 0,
    isExpandable: true,
    renderLayout(entryProps) {
      return <ArchivedFilter {...entryProps} />
    },
  })

  const archivedNotesEntries = archivedNotesSectionIsExpanded
    ? [archivedNotesSection, ...makeArchivedNotesEntries()]
    : [archivedNotesSection]

  const bottomEntries = [
    ...templateNotesEntries,
    makeEntry({
      id: 'import',
      depth: 0,
      isExpandable: false,
      renderLayout(entryProps) {
        return <ImportLink {...entryProps} />
      },
    }),
    ...archivedNotesEntries,
  ]

  return [scrollableEntries, bottomEntries]
}
